var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { OptionResolver } from "./core/OptionResolver";
import { StateReducer } from "./core/StateReducer";
import { StorageManager } from "./core/storage/StorageManager";
import { DomManager } from "./core/dom/DomManager";
import { ActionType } from "./core/StateReducer.types";
import { ColorModes } from "./types/ColorModes";
import { EventFactory } from "./core/events/EventFactory";
import { CustomEventTypes, PrefixedCustomEventTypes } from "./core/events/Events.types";
import { Component } from "component-lifecycle";
export class DarkModeToggle extends Component {
    constructor(element, opts = {}) {
        super(element);
        this.PREFIX = "darkmode";
        /**
         * Handles an external theme change event by updating the state and the DOM
         * if the root elements of the event and the component share roots.
         *
         * Implementation note: for performance reasons, DOM is only updated when the state is updated.
         * @private
         * @param e - The external theme change event
         */
        this.handleExternalThemeChange = (e) => {
            var _a, _b;
            const detail = e === null || e === void 0 ? void 0 : e.detail;
            if (!detail || typeof detail.isLight !== "boolean" || !Array.isArray(detail.roots)) {
                return;
            }
            const { isLight, roots: eventRoots } = detail;
            const thisRoots = (_a = this.dom) === null || _a === void 0 ? void 0 : _a.roots;
            const allRootsAffected = thisRoots === null || thisRoots === void 0 ? void 0 : thisRoots.every(root => eventRoots.includes(root));
            if (allRootsAffected && this.toggleState.do(ActionType.OVERRIDE, { isLight })) {
                (_b = this.dom) === null || _b === void 0 ? void 0 : _b.setState(this.toggleState.get());
            }
        };
        this.toggleOptions = OptionResolver.resolve(element, opts);
        this.toggleState = new StateReducer(this.toggleOptions.state, this.toggleOptions.lightColorMode, this.toggleOptions.darkColorMode);
    }
    /**
     * Factory method to create an instance of DarkModeToggle.
     * @param element the root element for the dark mode toggle component. The component will look for configuration options in this element's attributes.
     * @param opts the user provided options to configure the dark mode toggle instance. These options will override any configuration found in the element's attributes.
     * @returns A promise that resolves to the created and initialized DarkModeToggle instance
     */
    static create(element_1) {
        return __awaiter(this, arguments, void 0, function* (element, opts = {}) {
            const instance = new DarkModeToggle(element, opts);
            yield instance.init();
            yield instance.attach();
            return instance;
        });
    }
    doInit() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.storage = new StorageManager(this.toggleOptions.storage);
                this.applyPreferredScheme();
            }
            catch (error) {
                this.storage = undefined;
                return { cancelled: true, reason: `Storage error: ${error instanceof Error ? error.message : String(error)}` };
            }
            return { cancelled: false };
        });
    }
    doAttach() {
        return __awaiter(this, void 0, void 0, function* () {
            const dom = new DomManager(this.element, this.toggleOptions, (e) => {
                this.toggle();
                e.preventDefault();
            });
            this.dom = dom;
            this.element._bsDarkmodeToggle = this;
            this.setupCrossInstanceSync();
            this.syncState();
            return { cancelled: false };
        });
    }
    doDispose() {
        return __awaiter(this, void 0, void 0, function* () {
            globalThis.document.removeEventListener(PrefixedCustomEventTypes.CHANGE, this.handleExternalThemeChange);
            return { cancelled: false };
        });
    }
    doDestroy() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (this.isAttached())
                yield this.dispose();
            (_a = this.dom) === null || _a === void 0 ? void 0 : _a.destroy();
            delete this.element._bsDarkmodeToggle;
            return { cancelled: false };
        });
    }
    /**
     * Sets up an event listener to handle external theme change events.
     * When an external theme change event is triggered, this method updates the control state.
     * @private
     */
    setupCrossInstanceSync() {
        globalThis.document.addEventListener(PrefixedCustomEventTypes.CHANGE, this.handleExternalThemeChange);
    }
    /**
     * Syncs the state of the dark mode toggle by updating the DOM and persisting the current theme to storage.
     * @private
     */
    syncState() {
        var _a;
        (_a = this.dom) === null || _a === void 0 ? void 0 : _a.setState(this.toggleState.get());
        this.persistTheme();
    }
    toggle(silent = false) {
        this.ensureNotDestroyed();
        if (!this.toggleState.do(ActionType.TOGGLE))
            return;
        this.syncState();
        this.trigger(silent);
    }
    light(silent = false) {
        this.ensureNotDestroyed();
        if (!this.toggleState.do(ActionType.LIGHT))
            return;
        this.syncState();
        this.trigger(silent);
    }
    dark(silent = false) {
        this.ensureNotDestroyed();
        if (!this.toggleState.do(ActionType.DARK))
            return;
        this.syncState();
        this.trigger(silent);
    }
    setStorageType(type) {
        var _a;
        this.ensureNotDestroyed();
        (_a = this.storage) === null || _a === void 0 ? void 0 : _a.setStorageType(type);
        this.persistTheme();
    }
    /**
     * Triggers the events if silent is false.
     * The events are triggered with the current state of the dark mode toggle.
     * Emits the typed event via Component.emit and dispatches the legacy event manually.
     * @private
     * @param {boolean} silent - Whether to trigger the event.
     */
    trigger(silent) {
        var _a;
        if (silent)
            return;
        const legacyEvent = EventFactory.createLegacyEvent();
        this.element.dispatchEvent(legacyEvent);
        const roots = ((_a = this.dom) === null || _a === void 0 ? void 0 : _a.roots) || [];
        const currentState = this.toggleState.get();
        const eventDetail = EventFactory.createEventDetail(currentState, this.element, roots);
        this.emit(CustomEventTypes.CHANGE, eventDetail);
        roots.forEach((root) => {
            root.dispatchEvent(EventFactory.createPrefixedEvent(currentState, this.element, roots));
        });
    }
    /**
     * Persist the current theme to the storage.
     * @private
     */
    persistTheme() {
        var _a;
        (_a = this.storage) === null || _a === void 0 ? void 0 : _a.set(this.toggleState.get().theme);
    }
    /**
     * Applies the preferred color scheme based on cookies or system preference
     * @returns a boolean indicating whether a preference was applied (true) or not (false)
     * @throws Error on storage provider failure
     */
    applyPreferredScheme() {
        return this.applyStoredPreference() || this.applySystemPreference();
    }
    /**
     * Applies the color scheme based on stored preference if available
     * @returns a boolean indicating whether a preference was applied (true) or not (false)
     * @throws Error on storage provider failure
     */
    applyStoredPreference() {
        var _a;
        const value = (_a = this.storage) === null || _a === void 0 ? void 0 : _a.get();
        if (value === this.toggleOptions.darkColorMode) {
            this.toggleState.do(ActionType.DARK);
            return true;
        }
        if (value === this.toggleOptions.lightColorMode) {
            this.toggleState.do(ActionType.LIGHT);
            return true;
        }
        return false;
    }
    /**
     * Applies the color scheme based on system preferences if available
     * @returns a boolean indicating whether a preference was applied (true) or not (false)
     */
    applySystemPreference() {
        const systemPreference = this.getSystemPreference();
        if (systemPreference === ColorModes.DARK) {
            this.toggleState.do(ActionType.DARK);
            return true;
        }
        if (systemPreference === ColorModes.LIGHT) {
            this.toggleState.do(ActionType.LIGHT);
            return true;
        }
        return false;
    }
    /**
     * Gets the system color scheme preference if available
     * @returns color scheme preference as `ColorModes`
     */
    getSystemPreference() {
        var _a, _b;
        try {
            const darkModeQuery = (_a = globalThis.window) === null || _a === void 0 ? void 0 : _a.matchMedia("(prefers-color-scheme: dark)");
            const lightModeQuery = (_b = globalThis.window) === null || _b === void 0 ? void 0 : _b.matchMedia("(prefers-color-scheme: light)");
            if (darkModeQuery === null || darkModeQuery === void 0 ? void 0 : darkModeQuery.matches) {
                return ColorModes.DARK;
            }
            if (lightModeQuery === null || lightModeQuery === void 0 ? void 0 : lightModeQuery.matches) {
                return ColorModes.LIGHT;
            }
            return ColorModes.NONE;
        }
        catch (error) {
            console.warn("Unable to detect system color scheme preference:", error);
            return ColorModes.NONE;
        }
    }
    /**
     * Checks if the bs-darkmode-toggle instance has been destroyed.
     * If it has, throws an error indicating that the instance is no longer usable.
     * This is a safety measure to prevent accessing methods of a destroyed instance.
     * @throws {Error} If the instance has been destroyed.
     */
    ensureNotDestroyed() {
        if (this.isDestroyed())
            throw new Error("Accessing to a method of a destroyed bs-darkmode-toggle instance.");
    }
}
